// pages/secure/DealCompliancePage.js
// Wires the required Illinois disclosures/agreements into each transaction. Pick a
// deal, answer a few facts (residential? pre-1978? representation? escrow?), and the
// engine lists exactly which documents are required and why — with a print link for
// each blank form and a way to mark it collected. Assists agents, evidences
// compliance, and everything is retained 5 years (68 IAC 1450.755).

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';

const STATUS = {
    required: { badge: 'bg-amber-100 text-amber-700', label: 'needed' },
    collected: { badge: 'bg-green-100 text-green-700', label: 'collected' },
    waived: { badge: 'bg-slate-100 text-slate-600', label: 'waived' },
    na: { badge: 'bg-slate-100 text-slate-500', label: 'N/A' },
};

const DealCompliancePage = () => {
    const apiClient = useApiClient();
    const [overview, setOverview] = useState([]);
    const [error, setError] = useState('');
    const [openTxn, setOpenTxn] = useState(null);   // detail for a transaction
    const [profileForm, setProfileForm] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const loadOverview = useCallback(() => {
        apiClient.get('/api/transaction-requirements').then(setOverview).catch(err => setError(err.message));
    }, [apiClient]);
    useEffect(() => { loadOverview(); }, [loadOverview]);

    const openDeal = async (transaction_id) => {
        try {
            const d = await apiClient.get(`/api/transaction-requirements/${transaction_id}`);
            setOpenTxn(d);
            setProfileForm({
                is_residential: d.profile.is_residential !== 0,
                built_pre_1978: d.profile.built_pre_1978 === 1,
                representation: d.profile.representation || 'standard',
                interest_bearing_escrow: d.profile.interest_bearing_escrow === 1,
            });
        } catch (err) { setError(err.message); }
    };

    const generate = async () => {
        setIsSaving(true); setError('');
        try {
            await apiClient.post(`/api/transaction-requirements/${openTxn.transaction_id}/generate`, profileForm);
            const d = await apiClient.get(`/api/transaction-requirements/${openTxn.transaction_id}`);
            setOpenTxn(d);
            loadOverview();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const setStatus = async (req, status) => {
        try {
            await apiClient.put(`/api/transaction-requirements/requirement/${req.requirement_id}`, { status });
            const d = await apiClient.get(`/api/transaction-requirements/${openTxn.transaction_id}`);
            setOpenTxn(d);
            loadOverview();
        } catch (err) { setError(err.message); }
    };

    const printBlank = async (req) => {
        if (!req.doc_key) return;
        try {
            const doc = await apiClient.get(`/api/document-library/${req.doc_key}`);
            const w = window.open('', '_blank');
            w.document.write(`<html><head><title>${doc.title}</title><style>body{margin:40px;}@page{margin:1in;}</style></head><body>${doc.content}</body></html>`);
            w.document.close(); w.focus();
            setTimeout(() => w.print(), 300);
        } catch (err) { setError(err.message); }
    };

    return (
        <div className="p-6 max-w-5xl">
            <h1 className="text-2xl font-bold text-slate-800 mb-1">Deal Compliance</h1>
            <p className="text-slate-500 mb-6">Every deal's required disclosures and agreements — what's needed, what's collected, and the blank forms to print. Records retained 5 years (68 IAC 1450.755).</p>

            {error && <p className="mb-4 text-red-500">{error}</p>}

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <div className="p-4"><DataList rows={overview} rowKey="transaction_id" csvName="deal-compliance" defaultSort={{ key: 'pct', dir: 'asc' }} empty="No transactions yet."
                    filters={[{ key: 'transaction_type', label: 'Type' }, { key: 'transaction_status', label: 'Status' }]}
                    columns={[
                        { key: 'street_address', label: 'Transaction', value: (t) => t.street_address || `Transaction #${t.transaction_id}`, render: (t) => <span className="text-slate-700">{t.street_address || `Transaction #${t.transaction_id}`}</span> },
                        { key: 'transaction_type', label: 'Type' },
                        { key: 'transaction_status', label: 'Status' },
                        { key: 'pct', label: 'Compliance', type: 'number',
                            value: (t) => { const total = Number(t.total) || 0; return total ? Math.round(((Number(t.done) || 0) / total) * 100) : ''; },
                            render: (t) => {
                                const total = Number(t.total) || 0, done = Number(t.done) || 0;
                                const pct = total ? Math.round((done / total) * 100) : 0;
                                return total === 0 ? <span className="text-xs text-slate-400">not set up</span> : (
                                    <div className="flex items-center gap-2">
                                        <div className="w-24 bg-slate-100 rounded-full h-2"><div className={`h-2 rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} /></div>
                                        <span className="text-xs text-slate-500">{done}/{total}</span>
                                    </div>
                                );
                            } },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (t) => <button onClick={() => openDeal(t.transaction_id)} className="text-blue-600 hover:underline text-sm">{Number(t.total) ? 'Manage' : 'Set up'}</button> },
                    ]} /></div>
            </div>

            {/* Deal detail modal */}
            <Modal open={!!openTxn} onClose={() => { setOpenTxn(null); }} size="lg"
                   title={openTxn ? `Deal Compliance — Transaction #${openTxn.transaction_id} (${openTxn.transaction_type})` : ''}>
                {openTxn && profileForm && (
                    <div>
                        {/* Applicability questions */}
                        <div className="bg-slate-50 rounded-md p-4 mb-4">
                            <p className="text-sm font-medium text-slate-700 mb-2">Deal facts (these decide what's required)</p>
                            <div className="space-y-2 text-sm">
                                <label className="flex items-center gap-2"><input type="checkbox" checked={profileForm.is_residential} onChange={e => setProfileForm(p => ({ ...p, is_residential: e.target.checked }))} /> Residential property</label>
                                <label className="flex items-center gap-2"><input type="checkbox" checked={profileForm.built_pre_1978} onChange={e => setProfileForm(p => ({ ...p, built_pre_1978: e.target.checked }))} /> Built before 1978 (triggers Lead-Based Paint)</label>
                                <label className="flex items-center gap-2"><input type="checkbox" checked={profileForm.interest_bearing_escrow} onChange={e => setProfileForm(p => ({ ...p, interest_bearing_escrow: e.target.checked }))} /> Parties want interest-bearing escrow</label>
                                <div className="flex items-center gap-2">
                                    <span>Representation:</span>
                                    <select value={profileForm.representation} onChange={e => setProfileForm(p => ({ ...p, representation: e.target.value }))} className="border rounded px-2 py-1">
                                        <option value="standard">Standard (single agency)</option>
                                        <option value="dual_agency">Dual agency</option>
                                        <option value="designated_agency">Designated agency</option>
                                        <option value="no_agency">No agency (customer)</option>
                                    </select>
                                </div>
                            </div>
                            <button onClick={generate} disabled={isSaving} className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm">
                                {isSaving ? 'Updating...' : (openTxn.requirements.length ? 'Update required documents' : 'Generate required documents')}
                            </button>
                        </div>

                        {/* Requirements list */}
                        {openTxn.requirements.length === 0
                            ? <p className="text-slate-500 text-sm">Set the deal facts above and generate the required-document checklist.</p>
                            : (
                                <>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-medium text-slate-700">Required documents</p>
                                        <span className={`text-xs px-2 py-0.5 rounded ${openTxn.complete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {openTxn.complete ? '✓ complete' : `${openTxn.progress.outstanding} outstanding`}
                                        </span>
                                    </div>
                                    <div className="divide-y border rounded-md">
                                        {openTxn.requirements.map(req => (
                                            <div key={req.requirement_id} className="p-3">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-slate-700">{req.label}</p>
                                                        {req.statutory_ref && <p className="text-xs text-slate-400">{req.statutory_ref}</p>}
                                                    </div>
                                                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS[req.status].badge}`}>{STATUS[req.status].label}</span>
                                                </div>
                                                <div className="flex gap-3 mt-2 text-xs">
                                                    {req.doc_key && <button onClick={() => printBlank(req)} className="text-slate-600 hover:underline">🖨 Print blank</button>}
                                                    <button onClick={() => setStatus(req, 'collected')} className="text-green-600 hover:underline">Mark collected</button>
                                                    <button onClick={() => setStatus(req, 'waived')} className="text-slate-500 hover:underline">Waive</button>
                                                    <button onClick={() => setStatus(req, 'na')} className="text-slate-500 hover:underline">N/A</button>
                                                    {req.status !== 'required' && <button onClick={() => setStatus(req, 'required')} className="text-amber-600 hover:underline">Reset</button>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-slate-400 mt-3">Upload the signed copies in the transaction's documents / compliance section — those are the retained records. This checklist tracks that each required document is accounted for.</p>
                                </>
                            )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default DealCompliancePage;
